import { z } from 'zod';
import { SHA256_HEX_REGEX } from '../lib/sha256';
/**
 * Shared DTO primitives.
 *
 * Conventions (all modules follow these):
 * - All DTO keys are camelCase (the API layer maps to/from snake_case DB columns).
 * - Money/rate/qty values travel as fixed-scale decimal STRINGS matching the
 *   DB scales (amounts 14,2 · rates 14,6 · qty 14,4) — never floats.
 * - Percentages travel as plain finite JS numbers (converted via lib/decimal
 *   before any math).
 * - Timestamps are ISO-8601 strings; date-only values are `YYYY-MM-DD`.
 * - Response objects use `null` (not absent keys) for empty DB values.
 */
export const zUuid = z.string().uuid();
export const zSha256Hex = z.string().regex(SHA256_HEX_REGEX, 'expected lowercase sha256 hex');
export const zIsoDateTime = z.string().datetime({ offset: true });
export const zDateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
export const zEmail = z.string().trim().toLowerCase().email().max(320);
export const zHexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'expected #RRGGBB');
/** Two-letter US state code; `'US'` means federal in jurisdiction fields. */
export const zStateCode = z.string().regex(/^[A-Z]{2}$/, 'expected 2-letter code');
export const zJurisdiction = zStateCode;
/** Fixed-scale numeric string factory matching pg `numeric(precision, scale)`. */
export function numericString(precision, scale, opts = {}) {
    const intDigits = precision - scale;
    const sign = opts.nonNegative ? '' : '-?';
    const re = new RegExp(`^${sign}(0|[1-9]\\d{0,${intDigits - 1}})(\\.\\d{1,${scale}})?$`);
    return z
        .string()
        .regex(re, `expected a${opts.nonNegative ? ' non-negative' : ''} decimal string with up to ${intDigits} integer and ${scale} fractional digits`);
}
/** Amount string, numeric(14,2). */
export const zMoney = numericString(14, 2);
export const zMoneyNonNeg = numericString(14, 2, { nonNegative: true });
/** Rate string, numeric(14,6). */
export const zRate = numericString(14, 6);
export const zRateNonNeg = numericString(14, 6, { nonNegative: true });
/** Quantity string, numeric(14,4). */
export const zQty = numericString(14, 4);
export const zQtyNonNeg = numericString(14, 4, { nonNegative: true });
/** Percentage as a finite JS number (15 = 15%). */
export const zPct = z.number().finite();
export const zPctNonNeg = z.number().finite().min(0);
/**
 * Markup/margin value. The `mode` label is mandatory and the two modes have
 * different bounds: markup m ≥ 0 (price = cost × (1 + m)), margin 0 ≤ g < 100
 * (price = cost / (1 − g/100)) — margin 100 is schema-impossible.
 */
export const MarkupSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('markup'), pct: z.number().finite().min(0).max(10000) }).strict(),
    z.object({ mode: z.literal('margin'), pct: z.number().finite().min(0).lt(100) }).strict(),
]);
/** Estimate-level tax config. Tax applies to the PRICE of taxable lines after all layers. */
export const TaxSchema = z
    .object({
    enabled: z.boolean(),
    label: z.string().min(1).max(80),
    ratePct: z.number().finite().min(0).max(100),
})
    .strict();
// ------------------------------------------------------------------ envelopes
export const OkResponseSchema = z.object({ ok: z.literal(true) });
/** Standard error envelope (NestJS-compatible shape). */
export const ApiErrorSchema = z.object({
    statusCode: z.number().int(),
    error: z.string(),
    message: z.union([z.string(), z.array(z.string())]),
    details: z.unknown().optional(),
});
export const IdParamSchema = z.object({ id: zUuid });
/** Common list query (query-string values are coerced). */
export const PaginationQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
/** Paginated response wrapper. */
export function paginated(item) {
    return z.object({
        items: z.array(item),
        total: z.number().int().min(0),
        limit: z.number().int().min(1),
        offset: z.number().int().min(0),
    });
}
//# sourceMappingURL=common.js.map